import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';

export interface MultiSelectDropdownOptionDisplay {
  primary: string;
}

export type MultiSelectDropdownEditorParams = ICellEditorParams & {
  placeholder?: string;
  options?: any[];
  values?: () => any[];
  loadingData?: () => boolean;
  loadOptions?: () => void;
  getOptionId?: (option: any) => string | number;
  displayWith?: (option: any) => string;
  optionDisplayWith?: (option: any) => MultiSelectDropdownOptionDisplay;
  createOptionFromValue?: (value: string) => any;
  autocompletePanelClass?: string;
  onSelectionChange?: (
    selectedOptions: any[],
    params: ICellEditorParams
  ) => void;
  getSelectedValue?: (selectedOptions: any[]) => any;
};

@Component({
  selector: 'lib-multi-select-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './multi-select-dropdown.component.html',
  styleUrls: ['./multi-select-dropdown.component.scss'],
})
export class MultiSelectDropdownComponent
  implements ICellEditorAngularComp, AfterViewInit, OnDestroy
{
  private static activeEditor?: MultiSelectDropdownComponent;

  private params!: MultiSelectDropdownEditorParams;
  private documentKeydownListener?: (event: KeyboardEvent) => void;
  private documentMouseDownListener?: (event: MouseEvent) => void;
  private onBodyScrollListener?: () => void;
  private isClosing = false;
  private isDestroyed = false;

  public placeholder = '';
  public cellWidth = 0;
  public cellHeight = 0;
  public panelWidth: string | number = 'auto';
  public searchControl = new FormControl<string>('', { nonNullable: true });
  public autocompletePanelClass =
    'custom-autocomplete custom-autocomplete--multi-select';
  public isPanelOpen = false;
  public isLoading = false;
  public selectedOptions: any[] = [];
  public dropdownOptions: any[] = [];

  private getOptionId!: (option: any) => string | number;
  private displayWith!: (option: any) => string;
  private createOptionFromValue!: (value: string) => any;

  public optionDisplayWith!: (option: any) => MultiSelectDropdownOptionDisplay;

  @ViewChild('inputWithOptions')
  private readonly input!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger)
  private readonly trigger!: MatAutocompleteTrigger;

  constructor(private readonly hostElement: ElementRef<HTMLElement>) {}

  public agInit(params: MultiSelectDropdownEditorParams): void {
    if (
      MultiSelectDropdownComponent.activeEditor &&
      MultiSelectDropdownComponent.activeEditor !== this
    ) {
      MultiSelectDropdownComponent.activeEditor.closeFromAnotherEditor();
    }

    MultiSelectDropdownComponent.activeEditor = this;

    this.params = params;
    this.isClosing = false;
    this.isDestroyed = false;

    this.placeholder = params.placeholder || '';
    this.getOptionId =
      params.getOptionId ||
      ((option: any) =>
        option?.listId ??
        option?.itemCode ??
        option?.value ??
        option?.displayValue);
    this.displayWith =
      params.displayWith ||
      ((option: any) =>
        option?.displayValue ?? option?.itemCode ?? option?.value ?? '');

    this.createOptionFromValue =
      params.createOptionFromValue ||
      ((value: string) => ({ value, displayValue: value }));
    this.optionDisplayWith =
      params.optionDisplayWith ||
      ((option: any) => ({ primary: this.displayWith(option) }));
    this.autocompletePanelClass =
      params.autocompletePanelClass ||
      'custom-autocomplete custom-autocomplete--multi-select';

    const cell = params.eGridCell;
    this.cellWidth = cell.clientWidth + 4;
    this.cellHeight = cell.clientHeight + 4;
    this.panelWidth = Math.max(this.cellWidth, 220);

    params.loadOptions?.();

    this.dropdownOptions = this.getDropdownOptions();
    this.isLoading = params.loadingData?.() ?? false;
    this.selectedOptions = this.getInitialSelectedOptions(params.value);
    this.updateInputValue(false);

    this.onBodyScrollListener = () => this.closeAndStopEditing();
    this.params.api.addEventListener('bodyScroll', this.onBodyScrollListener);
  }

  public get optionsToRender(): any[] {
    return this.getDropdownOptions();
  }

  public get dropdownPanelClass(): string {
    const showCompactLoader =
      this.loader() && this.optionsToRender.length === 0;

    return `${this.autocompletePanelClass}${
      showCompactLoader ? ' custom-dropdown-multi-select--loading' : ''
    }`;
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.isDestroyed) return;

      this.input.nativeElement.focus();
      this.openPanel();
    });
  }

  public ngOnDestroy(): void {
    this.isDestroyed = true;

    if (this.trigger?.panelOpen) this.trigger.closePanel();

    if (this.onBodyScrollListener) {
      this.params.api.removeEventListener(
        'bodyScroll',
        this.onBodyScrollListener
      );
    }

    this.detachDocumentKeydownListener();
    this.detachDocumentMouseDownListener();

    if (MultiSelectDropdownComponent.activeEditor === this) {
      MultiSelectDropdownComponent.activeEditor = undefined;
    }
  }

  public loader(): boolean {
    return this.params?.loadingData?.() ?? this.isLoading;
  }

  public onFocus(event: FocusEvent): void {
    this.panelWidth = (event.target as HTMLInputElement).offsetWidth;
    this.openPanel();
  }

  public openPanel(): void {
    if (!this.trigger || this.isDestroyed) return;

    if (
      MultiSelectDropdownComponent.activeEditor &&
      MultiSelectDropdownComponent.activeEditor !== this
    ) {
      MultiSelectDropdownComponent.activeEditor.closeFromAnotherEditor();
    }

    MultiSelectDropdownComponent.activeEditor = this;
    this.isClosing = false;
    this.dropdownOptions = this.getDropdownOptions();
    this.input.nativeElement.focus();

    setTimeout(() => {
      if (!this.isDestroyed && !this.trigger.panelOpen) {
        this.trigger.openPanel();
      }
    });
  }

  public togglePanel(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.trigger) return;

    this.trigger.panelOpen ? this.closeAndStopEditing() : this.openPanel();
  }

  public onPanelOpen(): void {
    this.isPanelOpen = true;
    this.isClosing = false;
    MultiSelectDropdownComponent.activeEditor = this;
    this.attachDocumentKeydownListener();
    this.attachDocumentMouseDownListener();
  }

  public onPanelClose(): void {
    this.isPanelOpen = false;
    this.detachDocumentKeydownListener();
    this.detachDocumentMouseDownListener();

    if (this.isClosing || this.isDestroyed) return;

    this.closeAndStopEditing();
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.closeAndStopEditing();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeAndStopEditing(true);
      return;
    }

    if (event.key === 'ArrowDown' && !this.trigger?.panelOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.openPanel();
      return;
    }

    if (
      event.key.length === 1 ||
      event.key === 'Backspace' ||
      event.key === 'Delete'
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  public isSelected(option: any): boolean {
    const optionId = this.getOptionId(option);
    return this.selectedOptions.some(
      (selected) => this.getOptionId(selected) === optionId
    );
  }

  public onOptionRowClick(option: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleOption(option);
  }

  public toggleOption(option: any, event?: MatCheckboxChange): void {
    const optionId = this.getOptionId(option);
    const isChecked = event?.checked ?? !this.isSelected(option);

    if (isChecked) {
      const alreadySelected = this.selectedOptions.some(
        (selected) => this.getOptionId(selected) === optionId
      );

      if (!alreadySelected) {
        this.selectedOptions = [...this.selectedOptions, option];
      }
    } else {
      this.selectedOptions = this.selectedOptions.filter(
        (selected) => this.getOptionId(selected) !== optionId
      );
    }

    this.updateInputValue(false);
    this.notifySelectionChange();
  }

  public getValue(): any {
    return this.getSelectedGridValue();
  }

  public trackByOption = (_index: number, option: any): string | number =>
    this.getOptionId(option);

  private updateInputValue(emitEvent: boolean): void {
    const selectedText = this.selectedOptions
      .map((option) => this.displayWith(option))
      .filter(Boolean)
      .join(', ');
    this.searchControl.setValue(selectedText, { emitEvent });
  }

  private notifySelectionChange(): void {
    const field = this.params?.colDef?.field;
    const value = this.getSelectedGridValue();

    if (this.params.onSelectionChange) {
      this.params.onSelectionChange(this.selectedOptions, this.params);
    } else if (field && this.params.node) {
      this.params.node.setDataValue(field, value);
    }

    this.params.api.refreshCells({
      rowNodes: [this.params.node],
      force: true,
    });

    this.params.context?.onRowValidationChanged?.();
  }

  private getSelectedGridValue(): any {
    return this.params.getSelectedValue
      ? this.params.getSelectedValue(this.selectedOptions)
      : this.selectedOptions;
  }

  private getDropdownOptions(): any[] {
    if (Array.isArray(this.params.options)) return this.params.options;
    return this.params.values?.() ?? [];
  }

  private getInitialSelectedOptions(value: any): any[] {
    if (!value) return [];

    if (Array.isArray(value)) return value;

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => this.createOptionFromValue(item));
    }

    return [value];
  }

  private closeFromAnotherEditor(): void {
    if (this.isClosing || this.isDestroyed) return;

    this.isClosing = true;

    if (this.trigger?.panelOpen) this.trigger.closePanel();

    this.detachDocumentKeydownListener();
    this.detachDocumentMouseDownListener();

    setTimeout(() => {
      this.stopThisEditor();

      if (MultiSelectDropdownComponent.activeEditor === this) {
        MultiSelectDropdownComponent.activeEditor = undefined;
      }
    });
  }

  private closeAndStopEditing(cancel = false): void {
    if (this.isClosing || this.isDestroyed) return;

    this.isClosing = true;

    if (this.trigger?.panelOpen) this.trigger.closePanel();

    this.detachDocumentKeydownListener();
    this.detachDocumentMouseDownListener();

    setTimeout(() => {
      this.stopThisEditor(cancel);

      if (MultiSelectDropdownComponent.activeEditor === this) {
        MultiSelectDropdownComponent.activeEditor = undefined;
      }
    });
  }

  private stopThisEditor(cancel = false): void {
    const stopEditing = this.params?.stopEditing as
      | ((cancel?: boolean) => void)
      | undefined;

    if (typeof stopEditing === 'function') {
      stopEditing(cancel);
      return;
    }

    this.params?.api?.stopEditing(cancel);
  }

  private attachDocumentKeydownListener(): void {
    this.detachDocumentKeydownListener();

    this.documentKeydownListener = (event: KeyboardEvent) => {
      if (
        !this.trigger?.panelOpen ||
        (event.key !== 'Enter' && event.key !== 'Escape')
      )
        return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.closeAndStopEditing(event.key === 'Escape');
    };

    document.addEventListener('keydown', this.documentKeydownListener, true);
  }

  private detachDocumentKeydownListener(): void {
    if (!this.documentKeydownListener) return;

    document.removeEventListener('keydown', this.documentKeydownListener, true);
    this.documentKeydownListener = undefined;
  }

  private attachDocumentMouseDownListener(): void {
    this.detachDocumentMouseDownListener();

    this.documentMouseDownListener = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const clickedInsideEditor =
        this.hostElement.nativeElement.contains(target);
      const clickedInsideAutocompletePanel = !!target.closest(
        '.mat-mdc-autocomplete-panel, .mat-autocomplete-panel, .custom-autocomplete'
      );

      if (clickedInsideEditor || clickedInsideAutocompletePanel) return;

      this.closeAndStopEditing();
    };

    document.addEventListener(
      'mousedown',
      this.documentMouseDownListener,
      true
    );
  }

  private detachDocumentMouseDownListener(): void {
    if (!this.documentMouseDownListener) return;

    document.removeEventListener(
      'mousedown',
      this.documentMouseDownListener,
      true
    );
    this.documentMouseDownListener = undefined;
  }
}
